import React, { useEffect, useMemo, useState } from "react";
import { trackEvent } from "../utils/analytics";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://1wjd6u1zs8.execute-api.ap-southeast-2.amazonaws.com/prod";

const COLLECTION_OPTIONS = [
  {
    value: "end_of_season_drinks",
    label: "End of season drinks",
    description: "Chip in for a final round or team celebration."
  },
  {
    value: "coach_gift",
    label: "Coach gift",
    description: "Say thanks to the coach at the end of the season."
  },
  {
    value: "player_injury_support",
    label: "Player injury support",
    description: "Support a teammate with a team-approved collection."
  },
  {
    value: "club_equipment",
    label: "Club equipment",
    description: "Help with balls, bibs, cones, eskies or team gear."
  },
  {
    value: "team_event",
    label: "Team event",
    description: "Contribute towards a BBQ, presentation or social event."
  },
  {
    value: "other",
    label: "Other",
    description: "A general team collection."
  }
];

const QUICK_AMOUNTS = [5, 10, 20, 50];

function calculateCardFee(amountDollars) {
  const baseCents = Math.round(Number(amountDollars || 0) * 100);
  if (!baseCents || baseCents <= 0) {
    return { fee: 0, total: 0 };
  }

  // Keep this aligned with server.js calculateSimpleCardSurcharge().
  const percentageFee = 0.017;
  const fixedFeeCents = 30;
  const totalCents = Math.ceil((baseCents + fixedFeeCents) / (1 - percentageFee));
  const feeCents = Math.max(0, totalCents - baseCents);

  return {
    fee: feeCents / 100,
    total: totalCents / 100
  };
}

function getToken() {
  return localStorage.getItem("beercard_id_token") || localStorage.getItem("beercard_token") || "";
}

function getStoredUser() {
  return {
    email: localStorage.getItem("beercard_logged_in_email") || localStorage.getItem("beercard_email") || "",
    teamId: localStorage.getItem("beercard_teamId") || localStorage.getItem("teamId") || "",
    clubId: localStorage.getItem("beercard_clubId") || localStorage.getItem("clubId") || "",
    team: localStorage.getItem("beercard_team") || "",
    club: localStorage.getItem("beercard_club") || ""
  };
}

export default function Collections() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const [selectedType, setSelectedType] = useState(COLLECTION_OPTIONS[0].value);
  const [amount, setAmount] = useState(10);
  const [customAmount, setCustomAmount] = useState("");
  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedOption = COLLECTION_OPTIONS.find(option => option.value === selectedType) || COLLECTION_OPTIONS[0];
  const selectedAmount = customAmount ? Number(customAmount) : amount;
  const cardFee = calculateCardFee(selectedAmount);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pendingCollection = JSON.parse(sessionStorage.getItem("teamkitty_pending_collection") || "{}");
    if (params.get("success") === "true") {
      trackEvent("collection_completed", {
        collection_type: pendingCollection.collection_type,
        value: pendingCollection.value,
        currency: pendingCollection.currency || "AUD"
      });
      sessionStorage.removeItem("teamkitty_pending_collection");
    }
    if (params.get("cancelled") === "true") {
      trackEvent("collection_cancelled", {
        collection_type: pendingCollection.collection_type,
        value: pendingCollection.value,
        currency: pendingCollection.currency || "AUD"
      });
      sessionStorage.removeItem("teamkitty_pending_collection");
    }
  }, []);

  useEffect(() => {
    async function loadSummary() {
      if (!storedUser.teamId || !storedUser.clubId) return;

      setLoadingSummary(true);
      try {
        const params = new URLSearchParams({
          teamId: storedUser.teamId,
          clubId: storedUser.clubId
        });
        const res = await fetch(`${API_BASE}/collections/summary?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "x-user-email": storedUser.email
          }
        });

        if (!res.ok) throw new Error("Could not load collection totals");
        const data = await res.json();
        setSummary(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("Collection summary failed", err);
      } finally {
        setLoadingSummary(false);
      }
    }

    loadSummary();
  }, [storedUser.clubId, storedUser.email, storedUser.teamId]);

  const handleQuickAmount = (value) => {
    setAmount(value);
    setCustomAmount("");
  };

  const handlePay = async () => {
    setError("");
    trackEvent("collection_started", {
      collection_type: selectedType,
      value: selectedAmount,
      currency: "AUD"
    });

    if (!storedUser.email || !storedUser.teamId || !storedUser.clubId) {
      trackEvent("collection_failed", { reason: "missing_team_context", collection_type: selectedType });
      setError("Missing your TeamKitty team details. Please log out and back in.");
      return;
    }

    if (!Number.isFinite(selectedAmount) || selectedAmount < 2 || selectedAmount > 500) {
      trackEvent("collection_failed", { reason: "invalid_amount", collection_type: selectedType });
      setError("Choose an amount between $2 and $500.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/collections/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "x-user-email": storedUser.email
        },
        body: JSON.stringify({
          email: storedUser.email,
          amount: selectedAmount,
          collectionType: selectedType,
          teamId: storedUser.teamId,
          clubId: storedUser.clubId,
          success_url: `${window.location.origin}/collections?success=true`,
          cancel_url: `${window.location.origin}/collections?cancelled=true`
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start Stripe checkout");
      if (!data.url) throw new Error("Stripe checkout URL was not returned");

      trackEvent("collection_checkout_started", {
        collection_type: selectedType,
        value: selectedAmount,
        currency: "AUD"
      });
      sessionStorage.setItem("teamkitty_pending_collection", JSON.stringify({
        collection_type: selectedType,
        value: selectedAmount,
        currency: "AUD"
      }));
      window.location.href = data.url;
    } catch (err) {
      trackEvent("collection_failed", { reason: "checkout_session_error", collection_type: selectedType });
      setError(err.message || "Something went wrong starting checkout.");
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFF7E6] dark:bg-gray-950 px-4 py-6 text-[#0B1C2C] dark:text-white">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-3xl bg-white dark:bg-gray-900 shadow-lg border border-[#0B1C2C]/10 overflow-hidden">
          <div className="bg-[#0B1C2C] px-5 py-6 text-white">
            <p className="text-sm font-bold uppercase tracking-wide text-[#F59E0B]">TeamKitty</p>
            <h1 className="mt-1 text-3xl font-black">Collections</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/80">
              Contribute to a team collection using Stripe. Contributions are recorded against your team and do not top up your drink balance.
            </p>
          </div>

          <div className="grid gap-6 p-5 lg:grid-cols-[1.3fr_0.7fr]">
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-extrabold">What are you contributing to?</h2>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {COLLECTION_OPTIONS.map(option => {
                    const active = selectedType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedType(option.value)}
                        className={`rounded-2xl border p-4 text-left transition ${
                          active
                            ? "border-[#F59E0B] bg-[#FFF7E6] shadow-sm"
                            : "border-gray-200 bg-white hover:border-[#F59E0B]/70 dark:border-gray-700 dark:bg-gray-800"
                        }`}
                      >
                        <span className="block font-extrabold">{option.label}</span>
                        <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">{option.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <h2 className="text-lg font-extrabold">Choose amount</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {QUICK_AMOUNTS.map(value => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleQuickAmount(value)}
                      className={`rounded-full px-5 py-3 font-black transition ${
                        !customAmount && amount === value
                          ? "bg-[#0B1C2C] text-white"
                          : "bg-white text-[#0B1C2C] border border-gray-200 hover:bg-[#FFF7E6] dark:bg-gray-800 dark:text-white dark:border-gray-700"
                      }`}
                    >
                      ${value}
                    </button>
                  ))}
                </div>

                <label className="mt-4 block text-sm font-bold" htmlFor="customAmount">Custom amount</label>
                <div className="mt-2 flex max-w-xs items-center rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                  <span className="font-black">$</span>
                  <input
                    id="customAmount"
                    type="number"
                    min="2"
                    max="500"
                    step="1"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="ml-2 w-full bg-transparent outline-none"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                  {error}
                </div>
              )}

              <div className="rounded-2xl border border-[#F59E0B]/40 bg-[#FFF7E6] p-4 text-sm text-[#0B1C2C]">
                <div className="flex justify-between gap-4">
                  <span className="font-bold">Collection contribution</span>
                  <span className="font-black">${Number(selectedAmount || 0).toFixed(2)}</span>
                </div>
                <div className="mt-1 flex justify-between gap-4">
                  <span className="font-bold">Estimated card fee</span>
                  <span className="font-black">${cardFee.fee.toFixed(2)}</span>
                </div>
                <div className="mt-2 border-t border-[#F59E0B]/30 pt-2 flex justify-between gap-4 text-base">
                  <span className="font-black">Total charged by Stripe</span>
                  <span className="font-black">${cardFee.total.toFixed(2)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handlePay}
                disabled={submitting}
                className="w-full rounded-2xl bg-[#F59E0B] px-5 py-4 text-lg font-black text-[#0B1C2C] shadow-md transition hover:bg-[#E58F00] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? "Opening Stripe..." : `Contribute $${Number(selectedAmount || 0).toFixed(2)} + $${cardFee.fee.toFixed(2)} fee`}
              </button>
            </div>

            <aside className="rounded-3xl bg-[#0B1C2C] p-5 text-white">
              <h2 className="text-xl font-black">Collection summary</h2>
              <p className="mt-1 text-sm text-white/70">
                {storedUser.club || storedUser.clubId} {storedUser.team ? `• ${storedUser.team}` : ""}
              </p>

              <div className="mt-5 rounded-2xl bg-white/10 p-4">
                <p className="text-sm text-white/70">Selected</p>
                <p className="mt-1 text-lg font-black">{selectedOption.label}</p>
                <p className="mt-2 text-3xl font-black text-[#F59E0B]">${Number(selectedAmount || 0).toFixed(2)}</p>
                <p className="mt-1 text-sm text-white/70">Card fee ${cardFee.fee.toFixed(2)} • total ${cardFee.total.toFixed(2)}</p>
              </div>

              <div className="mt-5 space-y-3">
                <h3 className="font-extrabold">Raised so far</h3>
                {loadingSummary && <p className="text-sm text-white/70">Loading totals...</p>}
                {!loadingSummary && summary.length === 0 && (
                  <p className="text-sm text-white/70">No collection contributions yet.</p>
                )}
                {!loadingSummary && summary.map(item => (
                  <div key={item.collectionType} className="rounded-2xl bg-white/10 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-bold">{item.collectionLabel}</p>
                      <p className="font-black text-[#F59E0B]">${Number(item.total || 0).toFixed(2)}</p>
                    </div>
                    <p className="mt-1 text-xs text-white/60">{item.count} contribution{item.count === 1 ? "" : "s"}</p>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>
      </div>
    </main>
  );
}
